/**
 * Module used to detect what type of device the page is running on.
 * Uses a combination of a check for the presence of touch capabilities
 * and media queries against device width. Only differentiates between
 * 'desktop' and non desktop.
 *
 */
import UAParser from 'ua-parser-js';
import debounce from './debounce';

const deviceTypes = {
  pc: 'pc',
  tablet: 'tablet',
  mobile: 'mobile'
};
const listeners = [];
let screenParameters;
const orientations = {
  portrait: 'portrait',
  landscape: 'landscape'
};
/** cleanup function for listeners */
const cleanupListeners = () => {
  for (let i = listeners.length - 1; i >= 0; i--) {
    if (listeners[i].remove) {
      listeners.splice(i, 1);
    }
  }
};

const getBodyView = function() {
  return document.body.dataset.view || '';
};

const deviceUtils = {
  /**
   * The uaParser that determines what environment this is running in.
   * This is only exposed as a private property for testing.
   */
  _uaParser: UAParser,

  // eslint-disable-next-line object-shorthand
  deviceTypes: deviceTypes,
  /**
   * Convenience method for programmatically checking whether the app is
   * currently running in a desktop environment
   *
   * @returns boolean
   */
  // eslint-disable-next-line object-shorthand
  isDesktop: function() {
    return screenParameters.deviceType === deviceTypes.pc;
  },
  /**
   * Convenience method for programmatically checking if the app is running on
   * a mobile device or tablet.
   *
   * @returns boolean
   */
  // eslint-disable-next-line object-shorthand
  isHandheld: function() {
    return (
      screenParameters.deviceType === deviceTypes.mobile ||
      screenParameters.deviceType === deviceTypes.tablet ||
      getBodyView() === 'mobile'
    );
  },
  /**
   * Convenience method for programmatically checking if the app is running on
   * a mobile device.
   *
   * @returns boolean
   */
  // eslint-disable-next-line object-shorthand
  isMobile: function() {
    return (
      screenParameters.deviceType === deviceTypes.mobile ||
      getBodyView() === 'mobile'
    );
  },
  /**
   * Convenience method for programmatically checking if the app is running on
   * a tablet.
   *
   * @returns boolean
   */
  // eslint-disable-next-line object-shorthand
  isTablet: function() {
    return (
      screenParameters.deviceType === deviceTypes.tablet ||
      getBodyView() === 'mobile'
    );
  },
  /**
   * Returns the current parameters of the screen
   *
   * @returns An object containing the deviceType, orientation, height, and width of the viewport,
   * as well as a boolean flag `isDesktop` included for convenience.
   * deviceType can take three values: 'pc', 'mobile', or 'tablet'
   * orientation will either be 'landscape' or 'portrait'
   * height and width are measured in pixels.
   */
  // eslint-disable-next-line object-shorthand
  getScreenParameters: function() {
    return screenParameters;
  },
  /**
   * Set screen parameters of the screen
   */
  // eslint-disable-next-line object-shorthand
  setScreenParameters: function(screenParams) {
    screenParameters = screenParams;
  },
  /**
   * Indicates whether the device supports touch
   *
   * @returns boolean
   */
  // eslint-disable-next-line object-shorthand
  hasTouch: function() {
    return screenParameters.hasTouch;
  }
};

const cleanupLater = debounce(cleanupListeners, 250, false);
let onViewportChange = function() {
  const oldScreenParameters = screenParameters;
  screenParameters = {};
  screenParameters.hasTouch =
    'ontouchstart' in document.documentElement ||
    'ontouchstart' in window ||
    (window.DocumentTouch && document instanceof window.DocumentTouch);

  screenParameters.orientation = window.matchMedia('(orientation: landscape)')
    .matches
    ? orientations.landscape
    : orientations.portrait;

  screenParameters.width = window.screen.width;
  screenParameters.height = window.screen.height;
  // eslint-disable-next-line no-underscore-dangle
  const deviceInfo = deviceUtils._uaParser().device;

  screenParameters.deviceType = deviceInfo.type || 'pc';
  screenParameters.isDesktop = screenParameters.deviceType === 'pc';

  cleanupListeners();
  listeners.forEach(function(listener) {
    if (listener.listener === listener.callback) {
      listener.callback(screenParameters, oldScreenParameters);
    } else if (typeof listener.callback === 'string') {
      listener.listener[listener.callback](
        screenParameters,
        oldScreenParameters
      );
    } else {
      listener.callback.bind(listener.listener)(
        screenParameters,
        oldScreenParameters
      );
    }
  });
};

// Initialize parameters and then debounce so this function is not called repeatedly while the window is being
// resized
onViewportChange();
onViewportChange = debounce(onViewportChange, 250, false);

/**
 * Returns OS version name from UA Parser.
 *
 * @returns string maybe?
 */
deviceUtils.getOSName = function() {
  // eslint-disable-next-line no-underscore-dangle
  return this._uaParser().os.name;
};

/**
 * Returns OS version number from UA Parser.
 *
 * @param shouldRound - if version number should be rounded to whole number
 * @returns number
 */
deviceUtils.getOSVersion = function(shouldRound) {
  // eslint-disable-next-line no-underscore-dangle
  const version = parseFloat(this._uaParser().os.version);
  return shouldRound ? Math.round(version) : version;
};

/**
 * Convenience utility to check if app is running Android.
 *
 * @returns boolean
 */
deviceUtils.isAndroid = function() {
  return this.getOSName() === 'Android';
};

/**
 * HUI-4394: Detect for Android WebView based on UA string. Used in Android
 * embedded shell. See http://bit.ly/2ltZauu for more context.
 *
 * @returns boolean
 */
deviceUtils.isAndroidWebView = function() {
  if (!this.isAndroid()) {
    return false;
  }

  // TODO: extend regex to capture Chrome/ versions (WebView pre-Lollipop)
  // var isWebView = deviceUtils._uaParser().ua.match(/Chrome\/(\d+.\d)/g);

  // Android versions post Lollipop will have 'wv' in their UA string
  // eslint-disable-next-line no-underscore-dangle
  return this._uaParser().ua.match(/wv/g);
};

/**
 * A function for getting current orientation state.
 */
deviceUtils.getOrientation = function() {
  return window.matchMedia('(orientation: landscape)').matches
    ? 'landscape'
    : 'portrait';
};

/**
 * A function for determining iPhone or not.
 */
deviceUtils.isIOSDevice = function() {
  return deviceUtils.isHandheld() && deviceUtils.getOSName() === 'iOS';
};

/**
 * A function for determining iPhone 6+ or iPhone 7+.
 */
deviceUtils.isIPhonePlus = function() {
  return (
    deviceUtils.isIOSDevice() &&
    window.matchMedia('(-webkit-min-device-pixel-ratio: 3)').matches
  );
};

/**
 * A function for determining iPhone 6+ or iPhone 7+ browser shows a tab bar or not.
 */
deviceUtils.isIPhonePlusWithTabBar = function() {
  return deviceUtils.isIPhonePlus() && window.innerHeight < 370;
};

/**
 * A function for determining Chrome Browser.
 */
deviceUtils.isChrome = function() {
  return !!window.chrome && !!window.chrome.webstore;
};

/**
 * A function to check if IE11 browser.
 */
deviceUtils.isIE11 = function() {
  // eslint-disable-next-line spaced-comment
  return /*@cc_on!@*/ false || !!document.documentMode;
};

/**
 * A function for determining Edge Browser.
 */
deviceUtils.isEdge = function() {
  return !deviceUtils.isIE11() && !!window.StyleMedia;
};

/**
 * A function for toggle touchmove event on mobile devices.
 *
 * @param component - The web component need to be handled.
 * @param disable - to determine the component should disable touchmove or not.
 */
deviceUtils.stopTouchMove = function(component, disable) {
  if (component) {
    if (disable) {
      // eslint-disable-next-line no-undef
      component.addEventListener('touchmove', utils.stopEvent);
    } else {
      // eslint-disable-next-line no-undef
      component.removeEventListener('touchmove', utils.stopEvent, false);
    }
  }
};

/**
 * This function can be used to register a callback that will be fired when the orientation or size
 * of the viewport changes. Returns a handle that can be used to remove the registered callback.
 *
 * @param callback - The function that is called when the page is resized or the orientation
 * changes, or the name of the function to be called on listener. Will be passed the new screen parameters and the
 * old screen parameters, and will be executed in the context of the listener if provided.
 * @param listener - An optional object that will be used as the context for the callback. If callback is a
 * string, then a listener must be provided.
 * @returns A handle that can be used to unregister the callback
 */
deviceUtils.registerCallback = function(callback, listener) {
  if (!listener) {
    // eslint-disable-next-line no-param-reassign
    listener = callback;
  }

  listeners.push({
    // eslint-disable-next-line object-shorthand
    listener: listener,
    // eslint-disable-next-line object-shorthand
    callback: callback
  });
  return {
    // eslint-disable-next-line object-shorthand
    remove: function() {
      let i;
      for (i = listeners.length - 1; i >= 0; i--) {
        if (listeners[i].listener === listener) {
          // This could be called from within a callback, so just flag it
          // and it will be removed the next time onViewportChange fires.
          listeners[i].remove = true;
          cleanupLater();
        }
      }
    }
  };
};

window.addEventListener('orientationChange', onViewportChange);
window.addEventListener('resize', onViewportChange);
export default deviceUtils;
